import { Controller } from "@hotwired/stimulus"

// Manages the accordion wizard on the pesticide/other application edit page.
//
// The list for each step is always visible; only the add-form collapses.
// data-wizard-step-content="N" lives inside the turbo frame (in the partial),
// so open/closed state is tracked in JS and re-applied after frame reloads.
//
// Animation uses CSS grid-template-rows transition (0fr → 1fr) with an inner
// overflow-hidden wrapper — reliable for variable-height content.

export default class extends Controller {
  static values = { openStep: { type: Number, default: 2 } }

  connect() {
    this.stepOpen = { 2: false, 3: false }
    this._step1CompleteSeen = false

    // Detect step 1 completion to auto-open the next relevant step.
    // Uses a MutationObserver so it works for both full-page loads (just_saved
    // param) and Turbo Stream updates (no redirect, no page scroll).
    if (this.element.querySelector("[data-wizard-step1-complete]")) {
      this._step1CompleteSeen = true
      this.openStep(this.openStepValue)
      this._cleanUrl()
    }

    this._step1Observer = new MutationObserver(() => {
      if (!this._step1CompleteSeen && this.element.querySelector("[data-wizard-step1-complete]")) {
        this._step1CompleteSeen = true
        this.openStep(this.openStepValue)
        this._cleanUrl()
      }
    })
    this._step1Observer.observe(this.element, { childList: true, subtree: true })

    // Listen for frame reloads on steps 2 & 3 to re-apply collapse state
    this._frameListeners = []
    ;[2, 3].forEach(step => {
      const frame = document.getElementById(`wizard_step_${step}`)
      if (frame) {
        const handler = () => this.onStepFrameLoad(step)
        frame.addEventListener("turbo:frame-load", handler)
        this._frameListeners.push({ frame, handler })
      }
    })
  }

  disconnect() {
    this._step1Observer?.disconnect()
    this._frameListeners?.forEach(({ frame, handler }) => {
      frame.removeEventListener("turbo:frame-load", handler)
    })
    this._frameListeners = []
  }

  // Called whenever a step's turbo frame reloads
  onStepFrameLoad(step) {
    const frame = document.getElementById(`wizard_step_${step}`)
    if (frame?.querySelector("[data-wizard-edit-form]")) {
      // Edit form just loaded — open the step so the form is visible
      this.openStep(step)
    } else if (this.stepOpen[step]) {
      // Normal list+form reload — re-apply open state via openStep to enforce mutual exclusion
      this.openStep(step)
    }
  }

  toggle({ params: { step } }) {
    if (this.stepOpen[step]) {
      this.closeStep(step)
    } else {
      this.openStep(step)
    }
  }

  next({ params: { step } }) {
    this.closeStep(step)
    this.openStep(step + 1)
  }

  done({ params: { url } }) {
    const missing = []

    if (!this.element.querySelector("[id^='plantings_section'] li")) {
      missing.push("at least one planting (Step 2)")
    }
    if (!this.element.querySelector("[id^='pesticides_section'] li")) {
      missing.push("at least one pesticide (Step 3)")
    }
    if (document.getElementById("wizard_step_3_diluent")?.hasAttribute("data-diluent-missing")) {
      missing.push("total diluent amount (Step 3)")
    }

    if (missing.length > 0) {
      const dialog = document.getElementById("alert-dialog")
      document.getElementById("alert-dialog-title").textContent = "Not quite ready"
      const body = document.getElementById("alert-dialog-body")
      body.innerHTML = "Please complete the following before finishing:<ul class='list-disc list-inside mt-2 flex flex-col gap-1'>" +
        missing.map(m => `<li>${m}</li>`).join("") + "</ul>"
      dialog.showModal()
      return
    }

    window.location = url
  }

  openStep(step) {
    // Close the other collapsible steps first
    ;[2, 3].forEach(other => { if (other !== step) this.closeStep(other) })

    this.stepOpen[step] = true
    const el = this.stepContentEl(step)
    if (el) el.style.gridTemplateRows = "1fr"
    const chevron = this.chevronEl(step)
    if (chevron) chevron.style.transform = "rotate(180deg)"
    // Scroll the card into view after the transition completes
    const card = this.chevronEl(step)?.closest(".card")
    if (card) setTimeout(() => card.scrollIntoView({ behavior: "smooth", block: "nearest" }), 310)

    // Auto-show the diluent form when step 3 opens with no diluent entered
    if (step === 3) {
      const diluentSection = this.element.querySelector("[data-diluent-missing]")
      if (diluentSection) {
        const toggle = this.application.getControllerForElementAndIdentifier(diluentSection, "toggle")
        toggle?.show()
      }
    }
  }

  closeStep(step) {
    this.stepOpen[step] = false
    const el = this.stepContentEl(step)
    if (el) el.style.gridTemplateRows = "0fr"
    const chevron = this.chevronEl(step)
    if (chevron) chevron.style.transform = "rotate(0deg)"

    // Reset the diluent toggle when step 3 closes so the summary row shows on reopen
    if (step === 3) {
      const diluentSection = document.getElementById("wizard_step_3_diluent")
      if (diluentSection) {
        const toggle = this.application.getControllerForElementAndIdentifier(diluentSection, "toggle")
        toggle?.hide()
      }
    }
  }

  stepContentEl(step) {
    return this.element.querySelector(`[data-wizard-step-content="${step}"]`)
  }

  chevronEl(step) {
    const btn = this.element.querySelector(`[data-wizard-step-param="${step}"]`)
    return btn?.querySelector("[data-wizard-chevron]")
  }

  _cleanUrl() {
    const url = new URL(location.href)
    if (url.searchParams.has("just_saved")) {
      url.searchParams.delete("just_saved")
      history.replaceState(null, "", url.toString())
    }
  }
}
